import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { algoService, AlgoSignal, STRATEGIES, RISK_LABELS } from '../../services/algoService';
import { AlgoTradeChart } from './AlgoTradeChart';
import { canAccessAutoTrader } from '../../lib/entitlements';
import '../Dashboard.css';

function fmt(num: number | null | undefined, decimals = 2): string {
  if (num === null || num === undefined) return '—';
  return num.toFixed(decimals);
}

function fmtCurrency(num: number | null | undefined): string {
  if (num === null || num === undefined) return '—';
  return (num >= 0 ? '+' : '') + '$' + Math.abs(num).toFixed(2);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TradeRow: React.FC<{ signal: AlgoSignal }> = ({ signal }) => {
  const trade = signal.trade;
  if (!trade) return null;
  const strategy = STRATEGIES[signal.strategyId];
  const risk = RISK_LABELS[signal.riskAppetite];

  const pnl = trade.pnl;
  const pnlColor = pnl === null ? '#6b7280' : pnl >= 0 ? '#4ade80' : '#f87171';

  const statusColors: Record<string, string> = { submitted: '#d97706', open: '#2563eb', closed: '#374151', cancelled: '#6b7280' };

  return (
    <div style={{ background: '#1f2937', borderRadius: 8, padding: '14px 18px', marginBottom: 10,
      border: `1px solid ${trade.status === 'open' ? '#1e3a5f' : '#374151'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: '#f9fafb', fontWeight: 700, fontSize: 16 }}>{trade.symbol}</span>
          <span style={{
            background: ((trade as any).direction === 'short' || trade.side === 'sell') ? '#dc2626' : '#16a34a',
            color: '#fff', borderRadius: 4, padding: '1px 8px', fontSize: 12, fontWeight: 700,
          }}>
            {((trade as any).direction === 'short' || trade.side === 'sell') ? 'SHORT' : 'LONG'}
          </span>
          <span style={{ background: statusColors[trade.status] + '20', color: statusColors[trade.status],
            borderRadius: 4, padding: '1px 7px', fontSize: 11 }}>
            {trade.status.toUpperCase()}
          </span>
          <span style={{ color: '#a78bfa', fontSize: 11 }}>{strategy?.icon} {strategy?.name}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          {trade.status === 'closed' && (
            <div style={{ color: pnlColor, fontWeight: 700, fontSize: 16 }}>{fmtCurrency(pnl)}</div>
          )}
          {trade.status === 'submitted' && (
            <div>
              <span style={{ color: '#fbbf24', fontSize: 13, fontWeight: 600 }}>Order Pending</span>
              {trade.alpacaOrderStatus && (
                <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>
                  Alpaca: {trade.alpacaOrderStatus}
                </div>
              )}
            </div>
          )}
          {trade.status === 'open' && (
            <div>
              {trade.currentPrice != null
                ? <span style={{ color: '#93c5fd', fontWeight: 700, fontSize: 15 }}>${fmt(trade.currentPrice)}</span>
                : <span style={{ color: '#93c5fd', fontSize: 12 }}>Open position</span>}
              {trade.unrealizedPnl != null && (
                <span style={{
                  marginLeft: 8, fontWeight: 700, fontSize: 15,
                  color: trade.unrealizedPnl >= 0 ? '#4ade80' : '#f87171',
                }}>
                  {trade.unrealizedPnl >= 0 ? '+' : ''}${Math.abs(trade.unrealizedPnl).toFixed(2)}
                </span>
              )}
              {trade.lastCheckedAt && (
                <div style={{ color: '#6b7280', fontSize: 10, marginTop: 2 }}>
                  as of {fmtDate(trade.lastCheckedAt)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
        <div>
          <div style={{ color: '#6b7280', fontSize: 11 }}>Shares</div>
          <div style={{ color: '#e5e7eb' }}>{trade.qty}</div>
        </div>
        <div>
          <div style={{ color: '#6b7280', fontSize: 11 }}>Entry</div>
          <div style={{ color: '#e5e7eb' }}>{trade.filledPrice != null ? `$${fmt(trade.filledPrice)}` : trade.limitPrice ? `$${fmt(trade.limitPrice)} (limit)` : '—'}</div>
        </div>
        {trade.orderType === 'bracket' && (
          <div>
            <div style={{ color: '#6b7280', fontSize: 11 }}>Type</div>
            <div style={{ color: '#a78bfa', fontSize: 12 }}>Bracket</div>
          </div>
        )}
        {trade.status === 'closed' && trade.exitPrice && (
          <div>
            <div style={{ color: '#6b7280', fontSize: 11 }}>Exit</div>
            <div style={{ color: '#e5e7eb' }}>${fmt(trade.exitPrice)}</div>
          </div>
        )}
        {trade.status === 'open' && trade.suggestedStop && (
          <div>
            <div style={{ color: '#6b7280', fontSize: 11 }}>Stop</div>
            <div style={{ color: '#f87171' }}>${fmt(trade.suggestedStop)}</div>
          </div>
        )}
        {trade.status === 'open' && trade.suggestedTarget && (
          <div>
            <div style={{ color: '#6b7280', fontSize: 11 }}>Target</div>
            <div style={{ color: '#4ade80' }}>${fmt(trade.suggestedTarget)}</div>
          </div>
        )}
        <div>
          <div style={{ color: '#6b7280', fontSize: 11 }}>Opened</div>
          <div style={{ color: '#9ca3af', fontSize: 12 }}>{fmtDate(trade.entryAt)}</div>
        </div>
        {trade.exitAt && (
          <div>
            <div style={{ color: '#6b7280', fontSize: 11 }}>Closed</div>
            <div style={{ color: '#9ca3af', fontSize: 12 }}>
              {fmtDate(trade.exitAt)}
              {trade.exitReason && <span style={{ marginLeft: 6, color: '#6b7280' }}>({trade.exitReason})</span>}
            </div>
          </div>
        )}
        <div>
          <div style={{ color: '#6b7280', fontSize: 11 }}>Risk</div>
          <div style={{ color: risk.color, fontSize: 12 }}>{risk.label}</div>
        </div>
      </div>

      {trade.closeFailedAt && (
        <div style={{ marginTop: 8, padding: '6px 10px', background: '#7f1d1d30', borderRadius: 6, fontSize: 12, color: '#fca5a5' }}>
          Close failed at {fmtDate(trade.closeFailedAt)} — position may still be open on Alpaca
        </div>
      )}

      {trade.priceHistory && trade.priceHistory.length >= 2 && trade.filledPrice != null && (
        <AlgoTradeChart
          priceHistory={trade.priceHistory}
          entryPrice={trade.filledPrice}
          stopPrice={trade.suggestedStop}
          targetPrice={trade.suggestedTarget}
          symbol={trade.symbol}
        />
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const AlgoTrades: React.FC = () => {
  const { user, token } = useAuth();
  const [tradedSignals, setTradedSignals] = useState<AlgoSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrades = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await algoService.getTrades(token, { limit: 100 });
      setTradedSignals(result.signals);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const algoEnabled = canAccessAutoTrader(user, 'alpaca');

  // Compute P&L summary
  const closedTrades = tradedSignals.filter(s => s.trade?.status === 'closed');
  const openTrades = tradedSignals.filter(s => s.trade?.status === 'open');
  const submittedTrades = tradedSignals.filter(s => s.trade?.status === 'submitted');
  const totalPnl = closedTrades.reduce((sum, s) => sum + (s.trade?.pnl || 0), 0);
  const winCount = closedTrades.filter(s => (s.trade?.pnl || 0) > 0).length;
  const winRate = closedTrades.length > 0 ? (winCount / closedTrades.length) * 100 : 0;

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700, margin: 0 }}>
              Trade History <span className="beta-badge" style={{ fontSize: 11, marginLeft: 8 }}>BETA</span>
            </h1>
            <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>
              All executed trades from your algo engines with P&L tracking.
            </p>
          </div>

          {!algoEnabled && (
            <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 28, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
              <h2 style={{ color: '#f9fafb' }}>Algo Trading is a Pro Feature</h2>
              <p style={{ color: '#9ca3af' }}>Contact us to enable Algo Trading on your Pro account.</p>
            </div>
          )}

          {algoEnabled && (
            <>
              {/* P&L Summary */}
              {tradedSignals.length > 0 && (
                <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                  <div style={{ background: '#1f2937', borderRadius: 8, padding: '14px 20px', flex: 1, minWidth: 140 }}>
                    <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Total P&L (closed)</div>
                    <div style={{ color: totalPnl >= 0 ? '#4ade80' : '#f87171', fontSize: 22, fontWeight: 700 }}>
                      {fmtCurrency(totalPnl)}
                    </div>
                  </div>
                  <div style={{ background: '#1f2937', borderRadius: 8, padding: '14px 20px', flex: 1, minWidth: 140 }}>
                    <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Win Rate</div>
                    <div style={{ color: '#f9fafb', fontSize: 22, fontWeight: 700 }}>
                      {closedTrades.length > 0 ? `${winRate.toFixed(0)}%` : '—'}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 11 }}>{winCount}/{closedTrades.length} wins</div>
                  </div>
                  <div style={{ background: '#1f2937', borderRadius: 8, padding: '14px 20px', flex: 1, minWidth: 140 }}>
                    <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Open Trades</div>
                    <div style={{ color: '#93c5fd', fontSize: 22, fontWeight: 700 }}>{openTrades.length}</div>
                  </div>
                  <div style={{ background: '#1f2937', borderRadius: 8, padding: '14px 20px', flex: 1, minWidth: 140 }}>
                    <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Total Trades</div>
                    <div style={{ color: '#f9fafb', fontSize: 22, fontWeight: 700 }}>{tradedSignals.length}</div>
                  </div>
                </div>
              )}

              {/* Submitted (pending fill) trades section */}
              {submittedTrades.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h2 style={{ color: '#fbbf24', fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
                    Pending Orders ({submittedTrades.length})
                  </h2>
                  {submittedTrades.map(s => <TradeRow key={s.signalId} signal={s} />)}
                </div>
              )}

              {/* Open trades section */}
              {openTrades.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h2 style={{ color: '#93c5fd', fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
                    Open Positions ({openTrades.length})
                  </h2>
                  {openTrades.map(s => <TradeRow key={s.signalId} signal={s} />)}
                </div>
              )}

              {/* Closed trades section */}
              {loading ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading trades...</div>
              ) : error ? (
                <div style={{ color: '#f87171', background: '#7f1d1d', borderRadius: 8, padding: 16 }}>{error}</div>
              ) : tradedSignals.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>
                  No trades yet. Execute a pick from the Algo Research page to place your first trade.
                </div>
              ) : (
                <>
                  {closedTrades.length > 0 && (
                    <>
                      <h2 style={{ color: '#f9fafb', fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
                        Closed Trades ({closedTrades.length})
                      </h2>
                      {closedTrades.map(s => <TradeRow key={s.signalId} signal={s} />)}
                    </>
                  )}
                </>
              )}
            </>
          )}
      </div>
    </div>
  );
};

export default AlgoTrades;
